"use client";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
} from "react";
import { Button, DesignSurface, Select } from "./ui";
import {
  platform,
  items,
  type DesignSession,
  type DesignWebsite,
  type DesignRelease,
} from "./api";
import styles from "./ManagedEntry.module.css";
import { ManagedUnavailable } from "./ManagedUnavailable";
import { PersistentEditor } from "./PersistentEditor";
import { ManagedInvitationSetup } from "./ManagedInvitationSetup";
import { ManagedAuth } from "./ManagedAuth";
import { HostingBilling } from "./HostingBilling";
import { WebsitePlaybook } from "./WebsitePlaybook";
import { BuildyWorkspace } from "./BuildyWorkspace";

function initialOrganization(session: DesignSession) {
  const requested = Number(
    new URLSearchParams(window.location.search).get("org"),
  );
  let remembered = 0;
  try {
    remembered = Number(localStorage.getItem("buildy:active-organization"));
  } catch {
    /* preferences can be unavailable */
  }
  return (
    session.organizations.find(
      (item) => item.id === session.initial_context?.organization_id,
    )?.id ??
    session.organizations.find((item) => item.id === requested)?.id ??
    session.organizations.find((item) => item.id === remembered)?.id ??
    session.organizations.find((item) => item.is_agency)?.id ??
    session.organizations[0]?.id ??
    null
  );
}

function preferredWebsite(org: number): string | undefined {
  const params = new URLSearchParams(window.location.search);
  const requested =
    Number(params.get("org")) === org ? params.get("website") : null;
  try {
    return (
      requested ?? localStorage.getItem(`buildy:website:${org}`) ?? undefined
    );
  } catch {
    return requested ?? undefined;
  }
}

interface ManagedConfig {
  mode: "portal" | "editor";
  portalOrigin: string;
  website?: DesignWebsite;
  can_publish?: boolean;
  status?: string;
  releases?: DesignRelease[];
}
export function ManagedEntry({ Editor }: { Editor: ComponentType }) {
  return (
    <DesignSurface>
      <ManagedContent Editor={Editor} />
    </DesignSurface>
  );
}
function ManagedContent({ Editor }: { Editor: ComponentType }) {
  const [config, setConfig] = useState<ManagedConfig | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    void fetch("/__design/config", { credentials: "same-origin" })
      .then(async (r) => {
        if (!r.ok)
          throw new Error(
            "This workspace is unavailable. Return to your websites and open it again.",
          );
        setConfig((await r.json()) as ManagedConfig);
      })
      .catch((e) => setError(String(e.message)));
  }, []);
  if (error) return <ManagedUnavailable message={error} />;
  if (!config) return <main className={styles.center}>Opening Buildy…</main>;
  return config.mode === "editor" ? (
    <EditorShell config={config} Editor={Editor} />
  ) : (
    <Portal />
  );
}
function Portal() {
  const [setupMode, setSetupMode] = useState(
    () =>
      typeof window !== "undefined" &&
      window.location.pathname === "/account/setup",
  );
  const [session, setSession] = useState<DesignSession | null>(null);
  const [ready, setReady] = useState(false);
  const [org, setOrg] = useState<number | null>(null);
  const [sites, setSites] = useState<DesignWebsite[]>([]);
  const [websiteSelection, setWebsiteSelection] = useState<
    Record<number, string>
  >({});
  const loadedOrganization = useRef<number | null>(null);
  const selectWebsite = useCallback(
    (websiteId: string) => {
      if (org === null || loadedOrganization.current !== org) return;
      try {
        localStorage.setItem(`buildy:website:${org}`, websiteId);
      } catch {
        /* Optional preference. */
      }
      window.history.replaceState(
        null,
        "",
        `/?org=${org}&website=${encodeURIComponent(websiteId)}`,
      );
      setWebsiteSelection((current) => ({ ...current, [org]: websiteId }));
    },
    [org],
  );
  const updateWebsite = useCallback((website: DesignWebsite) => {
    setSites((current) =>
      current.map((item) => (item.id === website.id ? website : item)),
    );
  }, []);
  const addWebsite = useCallback(
    (website: DesignWebsite, organizationId: number) => {
      if (loadedOrganization.current !== organizationId) return;
      setSites((current) => [
        website,
        ...current.filter((item) => item.id !== website.id),
      ]);
      selectWebsite(website.id);
    },
    [selectWebsite],
  );
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [navigationDirty, setNavigationDirty] = useState(false);
  useEffect(() => {
    void platform<DesignSession>("design/session/")
      .then((s) => {
        setSession(s);
        setOrg(initialOrganization(s));
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);
  useEffect(() => {
    setError("");
    if (!session || org === null) {
      loadedOrganization.current = null;
      setSites([]);
      return;
    }
    // Refreshing billing/session data must not unmount the current editor draft.
    // A real organization switch still clears the previous account's inventory.
    if (loadedOrganization.current !== org) {
      loadedOrganization.current = org;
      setSites([]);
    }
    setSitesLoading(true);
    const controller = new AbortController();
    void platform<DesignWebsite[] | { results: DesignWebsite[] }>("websites/", {
      org,
      signal: controller.signal,
    })
      .then((v) => setSites(items(v)))
      .catch((e) => {
        if (!controller.signal.aborted) setError(String(e.message));
      })
      .finally(() => {
        if (!controller.signal.aborted) setSitesLoading(false);
      });
    return () => controller.abort();
  }, [org, session]);
  async function open(site: DesignWebsite, page = "/") {
    if (!session || org === null) return;
    if (session.persistent_editor) {
      window.location.assign(
        `/websites/${site.id}?org=${org}&page=${encodeURIComponent(page)}`,
      );
      return;
    }
    setBusy(site.id);
    setError("");
    try {
      type Workspace = { status: string; editor_url: string; handoff: string };
      let state = await platform<Workspace>(
        `design/websites/${site.id}/workspace/`,
        { method: "POST", body: {}, org, csrf: session.csrf_token },
      );
      const deadline = Date.now() + 5 * 60_000;
      while (!state.handoff && Date.now() < deadline) {
        if (state.status === "ERROR")
          throw new Error("The workspace could not start. Please try again.");
        await new Promise((resolve) => setTimeout(resolve, 2000));
        state = await platform<Workspace>(
          `design/websites/${site.id}/workspace/`,
          { org },
        );
      }
      if (!state.handoff)
        throw new Error(
          "The workspace is still starting. Please try opening it again shortly.",
        );
      const form = document.createElement("form");
      form.method = "POST";
      form.action = state.editor_url + "/__design/handoff";
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "token";
      input.value = state.handoff;
      form.append(input);
      document.body.append(form);
      form.submit();
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  }
  async function closeWorkspace(site: DesignWebsite) {
    if (!session || org === null) return;
    setBusy(`close:${site.id}`);
    setError("");
    try {
      await platform(`design/websites/${site.id}/workspace/`, {
        method: "DELETE",
        org,
        csrf: session.csrf_token,
      });
      type Workspace = { status: string };
      const deadline = Date.now() + 3 * 60_000;
      let state: Workspace;
      do {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        state = await platform<Workspace>(
          `design/websites/${site.id}/workspace/`,
          { org },
        );
      } while (state.status !== "STOPPED" && Date.now() < deadline);
      const value = await platform<
        DesignWebsite[] | { results: DesignWebsite[] }
      >("websites/", { org });
      setSites(items(value));
      if (state.status !== "STOPPED")
        setError("The workspace is still closing. Please try again shortly.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }
  async function logout() {
    if (!session) return;
    try {
      await platform("design/session/", {
        method: "DELETE",
        csrf: session.csrf_token,
      });
      setSession(null);
      setOrg(null);
      setSites([]);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  if (!ready) return <main className={styles.center}>Opening Buildy…</main>;
  if (setupMode)
    return (
      <ManagedInvitationSetup
        onLogin={(value) => {
          setSession(value);
          const selectedOrg = initialOrganization(value);
          setOrg(selectedOrg);
          window.history.replaceState(
            null,
            "",
            selectedOrg ? `/?org=${selectedOrg}` : "/",
          );
          setSetupMode(false);
        }}
      />
    );
  if (!session)
    return (
      <ManagedAuth
        onLogin={(s) => {
          setSession(s);
          setOrg(initialOrganization(s));
        }}
      />
    );
  if (window.location.pathname === "/playbook")
    return (
      <WebsitePlaybook
        session={session}
        onBack={() => window.location.assign("/")}
      />
    );
  const selected = session.organizations.find((o) => o.id === org);
  const websiteRoute =
    typeof window !== "undefined"
      ? /^\/websites\/([a-f0-9-]{36})\/?$/.exec(window.location.pathname)
      : null;
  if (session.persistent_editor) {
    return (
      <BuildyWorkspace
        session={session}
        org={org}
        sites={sites}
        onWebsiteUpdated={updateWebsite}
        onWebsiteCreated={addWebsite}
        onWebsiteActivated={(websiteId, organizationId) =>
          setSites((current) =>
            current.map((item) =>
              item.id === websiteId
                ? { ...item, customer_organization_id: organizationId }
                : item,
            ),
          )
        }
        loading={sitesLoading}
        error={error}
        busy={busy !== null}
        canvasWebsiteId={websiteRoute?.[1]}
        initialWebsiteId={
          org === null
            ? undefined
            : (websiteSelection[org] ?? preferredWebsite(org))
        }
        onWebsiteSelected={selectWebsite}
        initialPage={
          new URLSearchParams(window.location.search).get("page") ?? "/"
        }
        beforeNavigate={() =>
          !navigationDirty ||
          window.confirm("Leave with unsaved edits or an unsent message?")
        }
        onOrg={(id) => {
          try {
            localStorage.setItem("buildy:active-organization", String(id));
          } catch {
            /* optional preference */
          }
          if (websiteRoute) window.location.assign(`/?org=${id}`);
          else setOrg(id);
        }}
        onOpen={(site, page) => void open(site, page)}
        onLogout={() => void logout()}
        billing={
          <HostingBilling
            session={session}
            onUpdated={async () => {
              setSession(await platform<DesignSession>("design/session/"));
            }}
          />
        }
        hasUnsavedWork={navigationDirty}
        renderCanvas={
          org !== null && selected
            ? (
                site,
                back,
                navigationControl,
                page,
                onHistory,
                onPageChange,
                onSettings,
              ) => (
                <PersistentEditor
                  key={`${org}:${site.id}:${page}`}
                  initialPage={page}
                  unifiedSave={session.save_flow === true}
                  websiteId={site.id}
                  org={org}
                  session={session}
                  onBack={back}
                  onWebsiteUpdated={updateWebsite}
                  onNavigationDirty={setNavigationDirty}
                  navigationControl={navigationControl}
                  onOpenHistory={onHistory}
                  onOpenSettings={onSettings}
                  onPageChange={onPageChange}
                />
              )
            : undefined
        }
      />
    );
  }
  return (
    <main className={styles.portal}>
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Buildy</p>
          <h1>Your websites</h1>
        </div>
        <div className={styles.nav}>
          {session.guidance?.can_view && (
            <a href="/playbook">Website playbook</a>
          )}
          <Button onClick={() => void logout()}>Sign out</Button>
        </div>
      </header>
      <HostingBilling session={session} />
      <label className={styles.org}>
        Organization
        <Select
          disabled={busy !== null}
          value={org ?? ""}
          onChange={(e) =>
            setOrg(e.target.value ? Number(e.target.value) : null)
          }
        >
          <option value="">Choose an organization</option>
          {session.organizations.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </Select>
      </label>
      {error && <p role="alert">{error}</p>}
      <div className={styles.grid}>
        {sites.map((site) => (
          <article className={styles.card} key={site.id}>
            <h2>{site.name}</h2>
            <p>Source version {site.source_version}</p>
            <p>{site.live_release ? "Published" : "Not published"}</p>
            {site.hosting && !site.hosting.editing_enabled && (
              <p>
                Editing is paused. The account owner can manage hosting billing
                to restore access.
              </p>
            )}
            {selected?.can_edit ? (
              <Button
                variant="primary"
                disabled={
                  busy !== null ||
                  (!session.persistent_editor && site.can_write === false)
                }
                onClick={() => void open(site)}
              >
                {busy === site.id
                  ? "Starting your workspace…"
                  : site.can_write === false && session.persistent_editor
                    ? "View website"
                    : "Open editor"}
              </Button>
            ) : (
              <SavedPreview site={site} org={org!} />
            )}
            {selected?.can_edit &&
              !session.persistent_editor &&
              site.workspace &&
              site.workspace.status !== "STOPPED" && (
                <Button
                  disabled={busy !== null}
                  onClick={() => void closeWorkspace(site)}
                >
                  {busy === `close:${site.id}`
                    ? "Closing workspace…"
                    : "Close workspace"}
                </Button>
              )}
          </article>
        ))}
      </div>
      {org && sites.length === 0 && (
        <p>No websites are available in this organization.</p>
      )}
    </main>
  );
}
function SavedPreview({ site, org }: { site: DesignWebsite; org: number }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    void platform<DesignRelease[] | { results: DesignRelease[] }>(
      `websites/${site.id}/releases/`,
      { org },
    )
      .then((r) => {
        if (current)
          setUrl(
            site.review_url ??
              items(r).find((x) => x.id === site.preview_release)?.review_url ??
              null,
          );
      })
      .catch(() => {});
    return () => {
      current = false;
    };
  }, [site, org]);
  return url ? (
    <a href={url} target="_blank" rel="noreferrer">
      View saved preview
    </a>
  ) : (
    <span>No saved preview yet</span>
  );
}
function EditorShell({
  config,
  Editor,
}: {
  config: ManagedConfig;
  Editor: ComponentType;
}) {
  const [state, setState] = useState(config);
  const [error, setError] = useState("");
  const [accessError, setAccessError] = useState("");
  useEffect(() => {
    let mounted = true,
      pending = false,
      blocked = false;
    const controller = new AbortController();
    const poll = async () => {
      if (pending || blocked) return;
      pending = true;
      try {
        const r = await fetch("/__design/status", {
          credentials: "same-origin",
          signal: controller.signal,
        });
        if (!r.ok)
          throw new Error(
            r.status === 401 || r.status === 403
              ? "Your editor session has expired. Reopen the website to continue. Your saved work is safe."
              : "Your workspace connection was interrupted. Return to websites to reconnect. Your saved work is safe.",
          );
        const value = (await r.json()) as ManagedConfig;
        if (mounted) setState(value);
      } catch (e) {
        if (mounted) {
          blocked = true;
          clearInterval(timer);
          setAccessError(
            e instanceof Error && e.message.startsWith("Your ")
              ? e.message
              : "Your workspace connection was interrupted. Return to websites to reconnect. Your saved work is safe.",
          );
        }
      } finally {
        pending = false;
      }
    };
    const timer = setInterval(() => {
      void poll();
    }, 2500);
    void poll();
    return () => {
      mounted = false;
      clearInterval(timer);
      controller.abort();
    };
  }, []);
  async function publish(
    releaseId = state.website?.preview_release,
    restore = false,
  ) {
    setError("");
    try {
      const r = await fetch("/__design/publish", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          release_id: releaseId,
          expected_live_release: state.website?.live_release,
          request_key: crypto.randomUUID(),
          restore,
        }),
      });
      if (!r.ok)
        throw new Error(
          "Publication could not be requested. Refresh and try again.",
        );
    } catch (e) {
      setError((e as Error).message);
    }
  }
  if (accessError)
    return (
      <ManagedUnavailable
        message={accessError}
        portalOrigin={config.portalOrigin}
      />
    );
  return (
    <div className={styles.editor}>
      <header className={styles.editorBar}>
        <a href={config.portalOrigin}>← Websites</a>
        <strong>{state.website?.name}</strong>
        <span role="status">
          {error || state.status || "Opening workspace…"}
        </span>
        {state.can_publish && (
          <Button
            disabled={state.status !== "Ready for review"}
            onClick={() => void publish()}
          >
            Publish reviewed version
          </Button>
        )}
        <details className={styles.versions}>
          <summary>Versions</summary>
          <div>
            {state.releases?.map((release) => (
              <article key={release.id}>
                <strong>Source {release.source_version}</strong>
                {release.review_url && (
                  <a href={release.review_url} target="_blank" rel="noreferrer">
                    Review
                  </a>
                )}
                {state.can_publish && release.published_at && (
                  <Button
                    disabled={release.id === state.website?.live_release}
                    onClick={() => void publish(release.id, true)}
                  >
                    Restore publication
                  </Button>
                )}
              </article>
            ))}
          </div>
        </details>
      </header>
      <div className={styles.editorBody}>
        <Editor />
      </div>
    </div>
  );
}
